import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { fetchGetProductoDetalle } from '../services/productoService';
import { useUserContext } from '../hooks/UserContext';
import { Producto } from '../models/ProductoModel';
import { CurrencyFormatter } from '../utils/CurrencyFormatter';
import { 
    Box, 
    Typography, 
    CircularProgress, 
    Alert, 
    Card, 
    CardContent, 
    Grid, 
    Divider
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import QrCodeIcon from '@mui/icons-material/QrCode';

const ProductoDetalle = () => {
    const { getUser } = useUserContext();
    const { id: idProducto, id_local: idLocal } = useParams<{ id: string, id_local: string }>();
    const [producto, setProducto] = useState<Producto | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        fetchGetProductoDetalle(getUser()!, idLocal, idProducto!)
            .then((data) => {
                if (data && data.status !== 404) {
                    setProducto(data);
                } else {
                    setError('Producto no encontrado');
                }
            })
            .catch((error) => {
                console.error('Error fetching product details:', error);
                if (error.status === 404) {
                    setError('Producto no encontrado');
                } else {
                    setError('Error al cargar los datos del producto');
                }
            })
            .finally(() => {
                setLoading(false);
            });
    }, [getUser, idLocal, idProducto]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
                <CircularProgress size={60} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Alert severity="error" sx={{ maxWidth: 600 }}>{error}</Alert>
            </Box>
        );
    }

    if (!producto) {
        return (
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Alert severity="warning" sx={{ maxWidth: 600 }}>Producto no encontrado</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
                Detalle del Producto
            </Typography>
            
            <Card elevation={3} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 4 }}>
                    <Grid container spacing={3}>
                        {/* Product Name */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <ShoppingCartIcon color="primary" sx={{ fontSize: 32 }} />
                                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                    {producto.PRODUCTO}
                                </Typography>
                            </Box>
                            <Divider />
                        </Grid>

                        {/* Product Code */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <QrCodeIcon color="action" />
                                <Typography variant="body2" color="text.secondary">
                                    Código
                                </Typography>
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 'medium', ml: 4 }}>
                                {producto.CODIGO}
                            </Typography>
                        </Grid>

                        {/* Price Section */}
                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 2, 
                                p: 3, 
                                bgcolor: 'primary.light', 
                                borderRadius: 2,
                                color: 'primary.contrastText'
                            }}>
                                <AttachMoneyIcon sx={{ fontSize: 40 }} />
                                <Box>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Precio de Contado
                                    </Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                        {CurrencyFormatter.format(producto.CONTADO).replace("$","")}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </Box>
    );
};

export default ProductoDetalle;
